/**
 * Feb 2026 · Article Image Gallery.
 *
 * Drag-and-drop / browse upload zone + thumbnail strip + lightbox for a
 * single Article's image gallery. Everything talks to the backend directly
 * (multipart POST to /articles/:id/images) so the parent form can stay
 * unaware of the storage flow; on every mutation we `onChange(newImages)`
 * so the parent can update its local Article snapshot.
 */
import React, { useCallback, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { UploadSimpleIcon, StarIcon, TrashIcon, ArrowsClockwiseIcon, DownloadSimpleIcon, XIcon } from "@phosphor-icons/react";

const MAX_MB = 5;
const MAX_IMAGES = 10;
const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

// Prefer the axios base URL so we hit the right backend (works in preview and prod)
const backendUrl = () => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const imgSrc = (img) => `${backendUrl()}/api/files/articles/${encodeURIComponent(img.storage_path.replace(/^.*articles\//, ""))}`;

export default function ArticleImageGallery({ articleId, images = [], onChange, canEdit = true }) {
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [lightbox, setLightbox] = useState(null); // image obj or null
  const fileRef = useRef(null);
  const dragImgIdxRef = useRef(null);

  const sorted = useMemo(
    () => [...(images || [])].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0)),
    [images]
  );

  const canAddMore = sorted.length < MAX_IMAGES;

  const validate = (file) => {
    if (!ACCEPT.includes(file.type)) return `Unsupported type (${file.type || "?"}) — JPG / PNG / WEBP only`;
    if (file.size > MAX_MB * 1024 * 1024) return `Too large (${(file.size / 1024 / 1024).toFixed(1)}MB) — max ${MAX_MB}MB`;
    return null;
  };

  const uploadFiles = useCallback(async (files) => {
    if (!articleId) { toast.error("Save the article first, then upload images"); return; }
    const arr = Array.from(files || []);
    if (arr.length === 0) return;
    if (sorted.length + arr.length > MAX_IMAGES) {
      toast.error(`Max ${MAX_IMAGES} images per article — remove one first`);
      return;
    }
    setBusy(true);
    let ok = 0, fail = 0;
    for (const f of arr) {
      const err = validate(f);
      if (err) { toast.error(`${f.name}: ${err}`); fail++; continue; }
      try {
        const fd = new FormData();
        fd.append("file", f, f.name);
        const { data } = await api.post(`/articles/${articleId}/images`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        onChange?.(data.images || []);
        ok++;
      } catch (e) {
        fail++;
        toast.error(e?.response?.data?.detail || `Upload failed for ${f.name}`);
      }
    }
    setBusy(false);
    if (ok) toast.success(`${ok} image${ok > 1 ? "s" : ""} uploaded`);
  }, [articleId, sorted.length, onChange]);

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    if (!canEdit || !canAddMore) return;
    uploadFiles(e.dataTransfer.files);
  };

  const setPrimary = async (imgId) => {
    try {
      const { data } = await api.patch(`/articles/${articleId}/images/${imgId}`, { is_primary: true });
      onChange?.(data.images || []);
      toast.success("Primary image updated");
    } catch (e) { toast.error("Failed to set primary"); }
  };

  const removeImage = async (imgId) => {
    if (!window.confirm("Delete this image?")) return;
    try {
      const { data } = await api.delete(`/articles/${articleId}/images/${imgId}`);
      onChange?.(data.images || []);
      toast.success("Image removed");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const reorder = async (fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const next = [...sorted];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    // Optimistic — push new sort_order values then persist.
    const ordered = next.map((im, i) => ({ ...im, sort_order: i }));
    onChange?.(ordered);
    try {
      await api.patch(`/articles/${articleId}/images/reorder`, {
        order: ordered.map(o => o.id),
      });
    } catch (e) { toast.error("Reorder failed"); }
  };

  const download = (img) => {
    // Force download via anchor + blob so filename is preserved.
    const a = document.createElement("a");
    a.href = imgSrc(img);
    a.download = img.original_filename || "image";
    a.target = "_blank";
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-3" data-testid="article-image-gallery">
      {/* Drop zone */}
      {canEdit && canAddMore && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          data-testid="article-image-dropzone"
          className={`border-2 border-dashed rounded-sm px-6 py-8 text-center cursor-pointer transition-colors ${dragOver ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)] hover:border-[var(--accent)]/70"}`}
        >
          <UploadSimpleIcon size={28} className="mx-auto mb-2 text-[var(--accent)]" />
          <div className="text-sm font-semibold text-white">Drop images or click to browse</div>
          <div className="text-[11px] text-zinc-400 mt-1">JPG / PNG / WEBP · up to {MAX_MB}MB · max {MAX_IMAGES} images per article</div>
          <input
            ref={fileRef}
            type="file"
            multiple
            accept={ACCEPT.join(",")}
            className="hidden"
            data-testid="article-image-file-input"
            onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }}
          />
          {busy && <div className="text-xs text-amber-300 mt-2">Uploading…</div>}
        </div>
      )}

      {/* Thumbnail grid */}
      {sorted.length > 0 ? (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3" data-testid="article-image-thumbs">
          {sorted.map((img, idx) => (
            <div
              key={img.id}
              draggable={canEdit}
              onDragStart={() => { dragImgIdxRef.current = idx; }}
              onDragOver={(e) => { e.preventDefault(); }}
              onDrop={() => { const from = dragImgIdxRef.current; dragImgIdxRef.current = null; if (from != null) reorder(from, idx); }}
              className={`group relative rounded-sm border overflow-hidden bg-[var(--bg-0)] ${img.is_primary ? "border-amber-400/70" : "border-[var(--border)]"}`}
              data-testid={`article-image-thumb-${idx}`}
            >
              <img
                src={imgSrc(img)}
                alt={img.original_filename || `image-${idx}`}
                onClick={() => setLightbox(img)}
                className="w-full aspect-square object-cover cursor-zoom-in bg-black/40"
                style={{ maxHeight: 150, maxWidth: 150, margin: "0 auto" }}
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
              />
              {img.is_primary && (
                <span className="absolute top-1 left-1 text-[10px] font-bold px-1.5 py-0.5 rounded-sm bg-amber-500/80 text-black">PRIMARY</span>
              )}
              {canEdit && (
                <div className="absolute inset-x-0 bottom-0 flex bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!img.is_primary && (
                    <button title="Set as primary" data-testid={`article-image-primary-${idx}`} onClick={(e) => { e.stopPropagation(); setPrimary(img.id); }} className="flex-1 py-1 text-amber-300 hover:bg-amber-500/20"><StarIcon size={12} weight="regular" className="mx-auto" /></button>
                  )}
                  <button title="Download" onClick={(e) => { e.stopPropagation(); download(img); }} className="flex-1 py-1 text-zinc-300 hover:bg-white/10"><DownloadSimpleIcon size={12} className="mx-auto" /></button>
                  <button title="Delete" data-testid={`article-image-delete-${idx}`} onClick={(e) => { e.stopPropagation(); removeImage(img.id); }} className="flex-1 py-1 text-red-300 hover:bg-red-500/20"><TrashIcon size={12} className="mx-auto" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="tactical-card p-6 text-center text-xs text-zinc-500" data-testid="article-image-empty">
          No Image · Upload one to feature it on the Digital Catalogue.
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[999] bg-black/90 flex items-center justify-center p-6"
          data-testid="article-image-lightbox"
        >
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" data-testid="article-image-lightbox-close">
            <XIcon size={16} />
          </button>
          <img src={imgSrc(lightbox)} alt="preview" className="max-h-[92vh] max-w-[92vw] object-contain rounded-sm shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
